/**
 * Content Manager - AWS Configuration
 *
 * Configures AWS Amplify for Cognito authentication and API Gateway.
 * This replaces the Supabase client configuration.
 *
 * Configuration can be loaded at runtime from the API or fall back to
 * environment variables for local development.
 */

import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import type { ApiResponse } from '../types';
import { withRetry, fetchWithTimeout, getErrorMessage, isOnline, type ApiError } from './apiUtils';
import { logger } from './logger';
import { fetchRuntimeConfig, isNonProduction, type RuntimeConfig } from './runtimeConfig';
import { useAuthStore } from '../stores/authStore';
import { isAzureConfigured } from './msalConfig';
import { azureAuthService } from '../services/azureAuth';

// =============================================================================
// CONFIGURATION
// =============================================================================

// Mutable config that gets set at runtime
let awsConfig = {
  Auth: {
    Cognito: {
      userPoolId: '',
      userPoolClientId: '',
      signUpVerificationMethod: 'code' as const,
      loginWith: {
        email: true,
      },
    },
  },
};

// API Gateway base URL - set at runtime
let API_BASE_URL = '';

// AppSync Events endpoint for real-time - set at runtime
let APPSYNC_EVENTS_ENDPOINT = '';

/**
 * Apply runtime configuration to AWS settings
 */
function applyConfig(config: RuntimeConfig): void {
  awsConfig = {
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        signUpVerificationMethod: 'code' as const,
        loginWith: {
          email: true,
        },
      },
    },
  };
  API_BASE_URL = config.apiUrl;
  APPSYNC_EVENTS_ENDPOINT = config.appSyncEventsEndpoint || '';
}

/**
 * Initialize AWS Amplify with configuration
 * Fetches runtime config from the API if available, otherwise uses env vars
 */
export async function initializeAWS(): Promise<void> {
  logger.info('config', 'Initializing AWS configuration');

  // Fetch runtime configuration
  const config = await fetchRuntimeConfig();
  applyConfig(config);

  // Auto-enable debug logging for non-production environments (staging, dev, etc.)
  if (isNonProduction()) {
    logger.setConfig({
      enabled: true,
      level: 'DEBUG',
    });
    logger.info('config', 'Debug logging auto-enabled for non-production environment', {
      stage: config.stage,
    });
  }

  if (!awsConfig.Auth.Cognito.userPoolId || !awsConfig.Auth.Cognito.userPoolClientId) {
    // In local dev mode, this is expected - just log as info
    const isLocalDev = process.env.NODE_ENV === 'development' && API_BASE_URL?.startsWith('/');
    if (isLocalDev) {
      logger.info('auth', 'Running in local dev mode without Cognito auth');
      console.log('[Local Dev] Cognito auth not configured - API requests will be unauthenticated');
    } else {
      logger.warn('auth', 'AWS Cognito credentials not configured', {
        hasUserPoolId: Boolean(awsConfig.Auth.Cognito.userPoolId),
        hasClientId: Boolean(awsConfig.Auth.Cognito.userPoolClientId),
      });
      console.warn(
        'AWS Cognito credentials not configured. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID, or configure the /config/public endpoint.'
      );
    }
  }

  if (!API_BASE_URL) {
    logger.warn('api', 'API Gateway URL not configured');
    console.warn(
      'API Gateway URL not configured. Set REACT_APP_API_URL environment variable, or configure the /config/public endpoint.'
    );
  }

  Amplify.configure(awsConfig);
  logger.debug('config', 'AWS Amplify configured successfully', {
    hasApiUrl: Boolean(API_BASE_URL),
    hasAppSyncEndpoint: Boolean(APPSYNC_EVENTS_ENDPOINT),
    stage: config.stage,
  });
}

/**
 * Check if AWS is properly configured for authentication
 */
export function isAuthConfigured(): boolean {
  return Boolean(
    awsConfig.Auth.Cognito.userPoolId &&
    awsConfig.Auth.Cognito.userPoolClientId
  );
}

/**
 * Check if AWS is properly configured for API calls
 * Note: In local development, we may have API access without auth
 */
export function isAWSConfigured(): boolean {
  // For API calls, we only strictly need the API URL
  // Auth is optional (requests will work without token, just unauthenticated)
  return Boolean(API_BASE_URL);
}

// =============================================================================
// AUTHENTICATION EXPORTS
// =============================================================================

export { signIn, signOut, getCurrentUser };

/**
 * Get the current authentication session
 */
export async function getAuthSession() {
  logger.debug('auth', 'Fetching auth session');
  try {
    const session = await fetchAuthSession();
    logger.debug('auth', 'Auth session retrieved successfully', {
      hasTokens: Boolean(session.tokens),
    });
    return session;
  } catch (error) {
    logger.error('auth', 'Error fetching auth session', error);
    console.error('Error fetching auth session:', error);
    return null;
  }
}

/**
 * Get the JWT access token for API calls
 *
 * Token acquisition priority:
 * 1. Azure AD: Get fresh ID token using MSAL (auto-refreshes if needed)
 * 2. Cognito: Get access token from Cognito session
 * 3. Fallback: Use stored token from auth store (for dev bypass)
 */
export async function getAccessToken(): Promise<string | null> {
  logger.debug('auth', 'Getting access token');

  // If Azure AD is configured, try to get a fresh ID token
  // This handles token refresh automatically via MSAL
  if (isAzureConfigured()) {
    try {
      const tokens = await azureAuthService.getTokens();
      if (tokens?.idToken) {
        logger.debug('auth', 'Access token retrieved from Azure AD (fresh ID token)', { hasToken: true });
        // Update the auth store with the fresh token
        useAuthStore.getState().setToken(tokens.idToken);
        return tokens.idToken;
      }
      // If Azure is configured but token refresh failed, check if user is logged in
      const account = azureAuthService.getAccount();
      if (!account) {
        logger.debug('auth', 'Azure AD configured but no account found - user not logged in');
        // Clear stale auth state if the frontend thinks user is authenticated
        // but Azure AD session is gone (e.g., session expired, browser cache cleared)
        const authStore = useAuthStore.getState();
        if (authStore.isAuthenticated) {
          logger.info('auth', 'Clearing stale auth state - Azure AD session expired');
          authStore.logout();
        }
        return null;
      }
      // User is logged in but token refresh failed - fall back to stored token
      logger.warn('auth', 'Azure AD token refresh failed, falling back to stored token');
    } catch (error) {
      logger.error('auth', 'Error getting Azure AD token', error);
      // Fall through to try other methods
    }
  }

  try {
    // Try to get token from Cognito session
    const session = await fetchAuthSession();
    const cognitoToken = session.tokens?.accessToken?.toString() || null;

    if (cognitoToken) {
      logger.debug('auth', 'Access token retrieved from Cognito', { hasToken: true });
      return cognitoToken;
    }

    // Fall back to auth store token (for dev bypass or Azure fallback)
    const storeToken = useAuthStore.getState().accessToken;
    if (storeToken) {
      logger.debug('auth', 'Access token retrieved from auth store (fallback)', { hasToken: true });
      return storeToken;
    }

    logger.debug('auth', 'No access token available', { hasToken: false });
    return null;
  } catch (error) {
    // On error, still try auth store fallback
    const storeToken = useAuthStore.getState().accessToken;
    if (storeToken) {
      logger.debug('auth', 'Access token retrieved from auth store after Cognito error', { hasToken: true });
      return storeToken;
    }

    logger.error('auth', 'Error getting access token', error);
    console.error('Error getting access token:', error);
    return null;
  }
}

// =============================================================================
// API CLIENT
// =============================================================================

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  timeout?: number;
}

/**
 * Make an authenticated API request to API Gateway
 */
async function makeApiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    method = 'GET',
    body,
    headers = {},
    skipAuth = false,
    timeout = 30000,
  } = options;

  const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  logger.debug('api', `Starting API request [${method} ${endpoint}]`, {
    requestId,
    method,
    endpoint,
    hasBody: Boolean(body),
    skipAuth,
  });

  // Check online status
  if (!isOnline()) {
    logger.warn('api', `API request failed - offline [${method} ${endpoint}]`, { requestId });
    return {
      data: null,
      error: new Error('No internet connection. Please check your network.'),
    };
  }

  // Check configuration
  if (!isAWSConfigured()) {
    logger.error('api', `API request failed - AWS not configured [${method} ${endpoint}]`, {
      requestId,
      hasUserPoolId: Boolean(awsConfig.Auth.Cognito.userPoolId),
      hasClientId: Boolean(awsConfig.Auth.Cognito.userPoolClientId),
      hasApiUrl: Boolean(API_BASE_URL),
    });
    return {
      data: null,
      error: new Error('AWS is not configured'),
    };
  }

  try {
    // Get auth token if required
    let authHeaders: Record<string, string> = {};
    if (!skipAuth) {
      const token = await getAccessToken();
      if (token) {
        authHeaders = { Authorization: `Bearer ${token}` };
        logger.debug('api', `Auth token attached [${method} ${endpoint}]`, { requestId });
      } else {
        logger.warn('api', `No auth token available [${method} ${endpoint}]`, { requestId });
      }
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...headers,
    };

    const startTime = Date.now();
    const response = await withRetry(
      () =>
        fetchWithTimeout(url, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
          timeout,
        }),
      { maxRetries: 3 }
    );

    // Handle empty responses (e.g., 204 No Content from DELETE requests)
    // Some APIs return 200 with empty body, or may omit content-length header
    let data: T | null = null;
    const contentLength = response.headers.get('content-length');
    const hasNoContent = response.status === 204 || contentLength === '0';

    if (!hasNoContent) {
      const text = await response.text();
      if (text && text.trim()) {
        try {
          data = JSON.parse(text) as T;
        } catch (parseError) {
          // Log but don't fail - some successful responses may have non-JSON body
          logger.warn('api', `Failed to parse response JSON [${method} ${endpoint}]`, {
            requestId,
            text: text.substring(0, 100),
          });
        }
      }
    }
    const duration = Date.now() - startTime;

    logger.info('api', `API request successful [${method} ${endpoint}]`, {
      requestId,
      duration: `${duration}ms`,
      status: response.status,
    });

    return {
      data: data as T,
      error: null,
    };
  } catch (error) {
    logger.error('api', `API request failed [${method} ${endpoint}]`, {
      requestId,
      error: error instanceof Error ? error.message : String(error),
    });
    console.error(`API request failed [${method} ${endpoint}]:`, error);

    // Handle 401 errors by clearing stale auth state
    // This ensures users are redirected to login when their session expires
    const apiError = error as ApiError;
    if (apiError.statusCode === 401) {
      const authStore = useAuthStore.getState();
      if (authStore.isAuthenticated) {
        logger.info('api', 'Received 401 response - clearing auth state and redirecting to login');
        authStore.logout();
      }
    }

    return {
      data: null,
      error: new Error(getErrorMessage(error)),
    };
  }
}

// =============================================================================
// API CLIENT METHODS
// =============================================================================

export const apiClient = {
  /**
   * GET request
   */
  get<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return makeApiRequest<T>(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post<T>(endpoint: string, body: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return makeApiRequest<T>(endpoint, { ...options, method: 'POST', body });
  },

  /**
   * PUT request
   */
  put<T>(endpoint: string, body: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return makeApiRequest<T>(endpoint, { ...options, method: 'PUT', body });
  },

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return makeApiRequest<T>(endpoint, { ...options, method: 'PATCH', body });
  },

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return makeApiRequest<T>(endpoint, { ...options, method: 'DELETE' });
  },
};

// =============================================================================
// APPSYNC EVENTS (REAL-TIME)
// =============================================================================

export interface EventSubscription {
  unsubscribe: () => void;
}

export interface ContentChangeEvent {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  table: 'seeds' | 'outputs' | 'groups' | 'notes' | 'events';
  recordId: string;
  data?: unknown;
}

type EventCallback = (event: ContentChangeEvent) => void;

/**
 * AppSync Events client for real-time subscriptions
 * Uses WebSocket connection to AppSync Events API
 */
class AppSyncEventsClient {
  private socket: WebSocket | null = null;
  private subscribers: Map<string, Set<EventCallback>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;

  /**
   * Connect to AppSync Events WebSocket
   */
  async connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN || this.isConnecting) {
      logger.debug('realtime', 'WebSocket already connected or connecting');
      return;
    }

    if (!APPSYNC_EVENTS_ENDPOINT) {
      // Silently skip if AppSync Events is not configured - it's an optional feature
      logger.debug('realtime', 'AppSync Events endpoint not configured, skipping connection');
      return;
    }

    this.isConnecting = true;
    logger.info('realtime', 'Connecting to AppSync Events WebSocket');

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      // Construct WebSocket URL with auth
      const wsUrl = new URL(APPSYNC_EVENTS_ENDPOINT);
      wsUrl.searchParams.set('header', btoa(JSON.stringify({
        Authorization: `Bearer ${token}`,
        host: wsUrl.host,
      })));
      wsUrl.searchParams.set('payload', btoa('{}'));

      this.socket = new WebSocket(wsUrl.toString(), 'graphql-ws');

      this.socket.onopen = () => {
        logger.info('realtime', 'AppSync Events WebSocket connected');
        console.log('AppSync Events connected');
        this.reconnectAttempts = 0;
        this.isConnecting = false;

        // Send connection init
        this.socket?.send(JSON.stringify({ type: 'connection_init' }));
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          logger.error('realtime', 'Error parsing WebSocket message', error);
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = () => {
        logger.info('realtime', 'AppSync Events WebSocket disconnected');
        console.log('AppSync Events disconnected');
        this.isConnecting = false;
        this.attemptReconnect();
      };

      this.socket.onerror = (error) => {
        logger.error('realtime', 'AppSync Events WebSocket error', error);
        console.error('AppSync Events error:', error);
        this.isConnecting = false;
      };
    } catch (error) {
      logger.error('realtime', 'Failed to connect to AppSync Events', error);
      console.error('Failed to connect to AppSync Events:', error);
      this.isConnecting = false;
      this.attemptReconnect();
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(message: { type: string; payload?: ContentChangeEvent }): void {
    switch (message.type) {
      case 'connection_ack':
        logger.info('realtime', 'AppSync Events connection acknowledged');
        console.log('AppSync Events connection acknowledged');
        // Resubscribe to all channels
        this.subscribers.forEach((_, channel) => {
          this.sendSubscribe(channel);
        });
        break;

      case 'data':
        if (message.payload) {
          logger.debug('realtime', 'Received real-time event', {
            eventType: message.payload.eventType,
            table: message.payload.table,
            recordId: message.payload.recordId,
          });
          this.notifySubscribers(message.payload);
        }
        break;

      case 'error':
        logger.error('realtime', 'AppSync Events error', message);
        console.error('AppSync Events error:', message);
        break;
    }
  }

  /**
   * Send subscription request
   */
  private sendSubscribe(channel: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'subscribe',
        id: channel,
        payload: {
          channel: `/default/${channel}`,
        },
      }));
    }
  }

  /**
   * Notify subscribers of an event
   */
  private notifySubscribers(event: ContentChangeEvent): void {
    const channel = event.table;
    const callbacks = this.subscribers.get(channel);
    callbacks?.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in event callback:', error);
      }
    });

    // Also notify 'all' channel subscribers
    const allCallbacks = this.subscribers.get('all');
    allCallbacks?.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in event callback:', error);
      }
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('realtime', 'Max reconnection attempts reached', {
        maxAttempts: this.maxReconnectAttempts,
      });
      console.error('Max reconnection attempts reached');
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    logger.info('realtime', `Attempting to reconnect`, {
      delay: `${delay}ms`,
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
    });
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Subscribe to content changes
   */
  subscribe(channel: string, callback: EventCallback): EventSubscription {
    if (!this.subscribers.has(channel)) {
      this.subscribers.set(channel, new Set());

      // Send subscribe request if connected
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.sendSubscribe(channel);
      }
    }

    this.subscribers.get(channel)!.add(callback);

    // Ensure connection
    this.connect();

    return {
      unsubscribe: () => {
        const callbacks = this.subscribers.get(channel);
        if (callbacks) {
          callbacks.delete(callback);
          if (callbacks.size === 0) {
            this.subscribers.delete(channel);
            // Send unsubscribe if connected
            if (this.socket?.readyState === WebSocket.OPEN) {
              this.socket.send(JSON.stringify({
                type: 'unsubscribe',
                id: channel,
              }));
            }
          }
        }
      },
    };
  }

  /**
   * Disconnect and clean up
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.subscribers.clear();
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }
}

// Export singleton instance
export const eventsClient = new AppSyncEventsClient();

const awsExports = {
  initializeAWS,
  isAWSConfigured,
  apiClient,
  eventsClient,
  getAccessToken,
  getAuthSession,
  signIn,
  signOut,
  getCurrentUser,
};

export default awsExports;
